import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  AlertTriangle,
  Package,
  Download,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';

const AdminProducts = () => {
  const { fetchWithAuth } = useAdminAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    is_active: '',
    low_stock: false,
    brand: ''
  });
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [sortBy, setSortBy] = useState({ field: '', direction: 'asc' });

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: 20
      });
      
      if (search) params.append('search', search);
      if (filters.is_active !== '') params.append('is_active', filters.is_active);
      if (filters.low_stock) params.append('low_stock', 'true');
      if (filters.brand) params.append('brand', filters.brand);
      if (sortBy.field) {
        params.append('sort_by', sortBy.field);
        params.append('sort_direction', sortBy.direction);
      }

      const res = await fetchWithAuth(`/api/admin/products?${params}`);
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products);
        setPagination(prev => ({ ...prev, ...data.pagination }));
      }
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, pagination.page, search, filters, sortBy]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSort = (field) => {
    setSortBy(prev => {
      if (prev.field === field) {
        // Si ya está ordenado por este campo, cambiar dirección o quitar
        if (prev.direction === 'asc') {
          return { field, direction: 'desc' };
        } else {
          return { field: '', direction: 'asc' }; // Reset
        }
      }
      // Nuevo campo, empezar ascendente
      return { field, direction: 'asc' };
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const SortIcon = ({ field }) => {
    if (sortBy.field !== field) {
      return <ArrowUpDown size={14} className="text-gray-500" />;
    }
    return sortBy.direction === 'asc' 
      ? <ArrowUp size={14} className="text-neon-cyan" />
      : <ArrowDown size={14} className="text-neon-cyan" />;
  };

  const toggleVisibility = async (productId, currentStatus) => {
    try {
      const res = await fetchWithAuth(`/api/admin/products/${productId}/toggle-visibility`, {
        method: 'POST'
      });
      
      if (res.ok) {
        const data = await res.json();
        setProducts(prev => prev.map(p => 
          p.id === productId ? { ...p, is_active: data.is_active } : p
        ));
        toast.success(data.is_active ? 'Producto visible' : 'Producto oculto');
      }
    } catch (error) {
      toast.error('Error al cambiar visibilidad');
    }
  };

  const deleteProduct = async (productId, productName) => {
    const confirmMessage = `¿Estás seguro de ELIMINAR PERMANENTEMENTE el producto "${productName}"?\n\nEsta acción NO se puede deshacer.`;
    if (!window.confirm(confirmMessage)) return;
    
    try {
      // Use permanent=true to completely remove from database
      const res = await fetchWithAuth(`/api/admin/products/${productId}?permanent=true`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        setProducts(prev => prev.filter(p => p.id !== productId));
        toast.success('Producto eliminado permanentemente');
      }
    } catch (error) {
      toast.error('Error al eliminar producto');
    }
  };

  const exportProducts = async () => {
    try {
      const res = await fetchWithAuth('/api/admin/export/products');
      if (res.ok) {
        const data = await res.json();
        const blob = new Blob([data.csv_data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        a.click();
        toast.success('Exportación completada');
      }
    } catch (error) {
      toast.error('Error en exportación');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="space-y-6" data-testid="admin-products">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-white">Productos</h2>
          <p className="text-gray-400 text-sm">{pagination.total} productos en total</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={exportProducts}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            <Download size={18} />
            Exportar
          </button>
          <Link
            to="/admin/products/new"
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg transition-colors"
          >
            <Plus size={18} />
            Nuevo Producto
          </Link>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-gray-800 rounded-xl p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por código, nombre, marca..."
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:border-amber-500"
              />
            </div>
          </form>

          <div className="flex gap-3">
            <select
              value={filters.is_active}
              onChange={(e) => setFilters(prev => ({ ...prev, is_active: e.target.value }))}
              className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-amber-500"
            >
              <option value="">Todos los estados</option>
              <option value="true">Activos</option>
              <option value="false">Inactivos</option>
            </select>

            <button
              onClick={() => setFilters(prev => ({ ...prev, low_stock: !prev.low_stock }))}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-colors ${
                filters.low_stock 
                  ? 'bg-red-500/20 text-red-400 border border-red-500/50' 
                  : 'bg-gray-700 text-gray-300 border border-gray-600'
              }`}
            >
              <AlertTriangle size={16} />
              Stock bajo
            </button>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700/50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Producto</th>
                <th 
                  className="text-left px-4 py-3 text-sm font-medium text-gray-300 cursor-pointer hover:text-white transition-colors select-none"
                  onClick={() => handleSort('product_code')}
                >
                  <span className="flex items-center gap-2">
                    Código
                    <SortIcon field="product_code" />
                  </span>
                </th>
                <th 
                  className="text-left px-4 py-3 text-sm font-medium text-gray-300 cursor-pointer hover:text-white transition-colors select-none"
                  onClick={() => handleSort('inspired_by_brand')}
                >
                  <span className="flex items-center gap-2">
                    Marca
                    <SortIcon field="inspired_by_brand" />
                  </span>
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Precio</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">
                  <div className="flex flex-col">
                    <span>Stock</span>
                    <span className="text-xs text-gray-500">100ml / 35ml</span>
                  </div>
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Estado</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-300">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center text-gray-400">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500 mx-auto"></div>
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center text-gray-400">
                    <Package className="mx-auto mb-3" size={40} />
                    <p>No se encontraron productos</p>
                  </td>
                </tr>
              ) : (
                products.map(product => (
                  <tr key={product.id} className="hover:bg-gray-700/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={product.main_image || 'https://via.placeholder.com/40'}
                          alt={product.name}
                          className="w-10 h-10 object-cover rounded-lg"
                        />
                        <div className="min-w-0">
                          <p className="text-sm text-white font-medium truncate max-w-[200px]">
                            {product.name}
                          </p>
                          <p className="text-xs text-gray-400">
                            {product.inspired_by_name}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-300 font-mono">
                        {product.product_code}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-300">
                        {product.inspired_by_brand}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-white font-medium">
                        {formatCurrency(product.price)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${
                          product.stock === 0 ? 'text-red-400' :
                          product.stock <= 5 ? 'text-amber-400' :
                          'text-green-400'
                        }`}>
                          {product.stock}
                        </span>
                        <span className="text-gray-500">/</span>
                        <span className={`text-sm font-medium ${
                          (product.stock_40ml || 0) === 0 ? 'text-red-400' :
                          (product.stock_40ml || 0) <= 5 ? 'text-neon-cyan' :
                          'text-green-400'
                        }`}>
                          {product.stock_40ml || 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        product.is_active 
                          ? 'bg-green-500/10 text-green-400' 
                          : 'bg-gray-600 text-gray-400'
                      }`}>
                        {product.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => toggleVisibility(product.id, product.is_active)}
                          className="p-2 text-gray-400 hover:text-white transition-colors"
                          title={product.is_active ? 'Ocultar' : 'Mostrar'}
                        >
                          {product.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
                        </button>
                        <Link
                          to={`/admin/products/${product.id}`}
                          className="p-2 text-gray-400 hover:text-amber-500 transition-colors"
                          title="Editar"
                        >
                          <Edit size={16} />
                        </Link>
                        <button
                          onClick={() => deleteProduct(product.id, product.name)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                          title="Eliminar permanentemente"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
            <p className="text-sm text-gray-400">
              Mostrando {(pagination.page - 1) * 20 + 1} - {Math.min(pagination.page * 20, pagination.total)} de {pagination.total}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-3 py-1.5 bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
              >
                Anterior
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
                className="px-3 py-1.5 bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProducts;
